import React from 'react';
import { useTranslation } from 'react-i18next';

/**
 * "View" anchor for an attached spray-instruction PDF.
 *
 * Behaviour preserved from the inline implementation that previously lived
 * in SpraySessionDetail.tsx:
 *
 * - The element is an `<a>` (not a button) so the right-click "Open in new
 *   tab" semantic still works against the GET /api/v1/spray/instructions/:id/pdf
 *   route. Tests assert on this anchor shape.
 * - Default click is intercepted (e.preventDefault) so we can fetch a
 *   presigned S3 URL via the service and point a new tab at it. The new
 *   window is opened *synchronously* inside the click gesture so the
 *   browser's popup blocker does not swallow it. Critically we do NOT pass
 *   'noopener,noreferrer' as window.open features — those flags cause
 *   window.open to return null and force navigation onto the current tab.
 *   The signed S3 URL is server-controlled and the cross-origin S3 page
 *   cannot read window.opener, so the residual exposure is acceptable.
 * - When `pdfKey` is missing the anchor renders disabled (Bootstrap
 *   `disabled` class, `aria-disabled="true"`, `tabIndex={-1}`) and
 *   suppresses navigation. The "PDF unavailable" badge is rendered by the
 *   parent row.
 */
export interface ViewInstructionAnchorProps {
  instructionId: string;
  hasPdf: boolean;
  fetchSignedUrl: (id: string) => Promise<string>;
  onError?: (message: string) => void;
}

const ViewInstructionAnchor: React.FC<ViewInstructionAnchorProps> = ({
  instructionId,
  hasPdf,
  fetchSignedUrl,
  onError,
}) => {
  const { t } = useTranslation();
  const href = `/api/v1/spray/instructions/${instructionId}/pdf`;

  if (!hasPdf) {
    return (
      <a
        className="btn btn-outline-secondary btn-sm disabled"
        aria-disabled="true"
        tabIndex={-1}
        href={href}
        onClick={(e) => e.preventDefault()}
      >
        {t('common.view')}
      </a>
    );
  }

  return (
    <a
      className="btn btn-outline-secondary btn-sm"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={async (e) => {
        e.preventDefault();
        if (onError) onError('');
        // Open the new tab synchronously inside the user gesture
        // (popup-blocker workaround). Do NOT pass 'noopener,noreferrer' here
        // — those flags cause window.open to return null, leaving the new
        // tab blank and forcing navigation onto the current window.
        const win = window.open('about:blank', '_blank');
        try {
          // Get a presigned S3 URL via JSON, then have the new tab navigate
          // to it directly. This avoids the CORS preflight that an axios
          // redirect to S3 would trigger from a localhost dev origin.
          const url = await fetchSignedUrl(instructionId);
          if (win) {
            win.location.href = url;
          } else {
            // Popup blocked — fall back to same-tab nav.
            window.location.href = url;
          }
        } catch (err: any) {
          if (win) win.close();
          if (onError) onError(err?.message ?? t('spray.instructions.loadError'));
        }
      }}
    >
      {t('common.view')}
    </a>
  );
};

export default ViewInstructionAnchor;
